import { customValidator } from './fieldValidators'
import * as IpAddress from 'ip-address'

export enum NetworkStackTypes {
  IPv4 = 'ipv4',
  IPv6 = 'ipv6',
}

export const isValidIpv4Address = (address) => {
  try {
    // Test if IpAddress lib works as well as contains a /
    const testAddress = new IpAddress.Address4(address)
    return true
  } catch (err) {
    return false
  }
}

export const isValidIpv6Address = (address) => {
  try {
    // Test if IpAddress lib works as well as contains a /
    const testAddress = new IpAddress.Address6(address)
    return true
  } catch (err) {
    return false
  }
}

export const isValidIpv4Cidr = (cidr) => {
  try {
    // Test if IpAddress lib works as well as contains a /
    const testAddress = new IpAddress.Address4(cidr)
    return cidr.includes('/')
  } catch (err) {
    return false
  }
}

export const isValidIpv6Cidr = (cidr) => {
  // Test if IpAddress lib works as well as contains a /
  try {
    const testAddress = new IpAddress.Address6(cidr)
    return cidr.includes('/')
  } catch (err) {
    return false
  }
}

const getIpValidator = (type = NetworkStackTypes.IPv4) =>
  ({
    [NetworkStackTypes.IPv4]: {
      cls: IpAddress.Address4,
      blockSize: [20, 32],
      subnetMask: [0, 32],
    },
    [NetworkStackTypes.IPv6]: {
      cls: IpAddress.Address6,
      blockSize: [116, 128],
      subnetMask: [0, 128],
    },
  }[type])

const isValidBlockSize = (size, stack) => {
  const {
    blockSize: [lowerBound, upperBound],
  } = getIpValidator(stack)
  return size >= lowerBound && size <= upperBound
}
const isValidSubnetMask = (size, stack) => {
  const {
    subnetMask: [lowerBound, upperBound],
  } = getIpValidator(stack)
  return size >= lowerBound && size <= upperBound
}

const IPValidator = customValidator((value, formValues) => {
  const { cls: IPValidatorCls } = getIpValidator(formValues.networkStack)
  return IPValidatorCls.isValid(value)
}, 'Invalid IP address provided')

const cidrIndependenceValidator = customValidator((value, formValues) => {
  const { cls: IPValidatorCls } = getIpValidator(formValues.networkStack)
  const containersIPInstance = new IPValidatorCls(formValues.containersCidr)
  const servicesIPInstance = new IPValidatorCls(value)

  return (
    servicesIPInstance.isInSubnet(containersIPInstance) === false &&
    containersIPInstance.isInSubnet(servicesIPInstance) === false
  )
}, 'Services CIDR must be an independent subnet of Containers CIDR')

const ipv6BlockSizeValidator = customValidator((value, formValues) => {
  const blockSize = parseInt(value)
  return isValidBlockSize(blockSize, formValues.networkStack)
}, 'Block Size must be in the range 116 -> 128')

const ipv4BlockSizeValidator = customValidator((value, formValues) => {
  const blockSize = parseInt(value)
  return isValidBlockSize(blockSize, formValues.networkStack)
}, 'Block Size must be in the range 20 -> 32')

const ipv6SubnetMaskSizeValidator = customValidator((value, formValues) => {
  const subnetMask = parseInt(`${value}`.split('/')[1])
  return isValidSubnetMask(subnetMask, formValues.networkStack)
}, 'Subnet Mask must be in the range 116 -> 128')

const ipv4SubnetMaskSizeValidator = customValidator((value, formValues) => {
  const subnetMask = parseInt(`${value}`.split('/')[1])
  return isValidSubnetMask(subnetMask, formValues.networkStack)
}, 'Subnet Mask must be in the range 20 -> 32')

const calicoBlockSizeCIDRValidator = customValidator((value, formValues) => {
  const cidrRange = formValues.containersCidr || formValues.clusterPodCidr
  const blockSize = parseInt(`${cidrRange}`.split('/')[1])
  if (!isValidSubnetMask(blockSize, formValues.networkStack)) {
    return false
  }
  return value >= blockSize
}, 'Calico Block Size must not conflict with the Container CIDR subnet mask')

const secondaryCIDRBlockValidator = customValidator((value, formValues) => {
  const { cls: IPValidatorCls } = getIpValidator(formValues.networkStack)
  const ipInstance = new IPValidatorCls(value)
  return (
    ipInstance.isInSubnet(new IPValidatorCls('100.64.0.0/10')) === true ||
    ipInstance.isInSubnet(new IPValidatorCls('198.19.0.0/16')) === true
  )
}, 'Must be within the 100.64.0.0/10 or 198.19.0.0/16 range.')

export const ipValidators = {
  [NetworkStackTypes.IPv4]: {
    cidrIndependenceValidator,
    calicoBlockSizeCIDRValidator,
    subnetMaskSizeValidator: ipv4SubnetMaskSizeValidator,
    ipValidator: IPValidator,
    blockSizeValidator: ipv4BlockSizeValidator,
    secondaryCIDRBlockValidator,
  },
  [NetworkStackTypes.IPv6]: {
    cidrIndependenceValidator,
    calicoBlockSizeCIDRValidator,
    subnetMaskSizeValidator: ipv6SubnetMaskSizeValidator,
    ipValidator: IPValidator,
    blockSizeValidator: ipv6BlockSizeValidator,
    secondaryCIDRBlockValidator,
  },
}
export const arnAwsValidator = customValidator((value) => {
  const regex = /^(arn:(?:aws|aws-cn|aws-us-gov):[\w\d-]+:[\w\d-]*:\d{0,12}:[\w\d-]*\/?[\w\d-]*)(\/.*)?.*$/
  return value.match(regex)
}, 'Invalid AWS ARN Value')

export const ipv4CidrValidator = customValidator((value) => {
  if (!value) return true
  const regex = /^([0-9]{1,3}\.){3}[0-9]{1,3}(\/([0-9]|[1-2][0-9]|3[0-2]))?$/
  return value.match(regex)
}, 'Invalid IP/CIDR  Notation')

// This is not a comprehensive validator, using this because ssh-pub-key-validation stopped working
export const sshKeyValidator = customValidator((value) => {
  const regex = /^ssh-rsa\s+[A-Za-z0-9+/]+[=]{0,3}(\s+.+)?\s*$/
  return value.match(regex)
}, 'You must enter a valid SSH key')

export const sshKeyTypeBasedValidator = (keyType: string) =>
  customValidator((value: string) => {
    const regexMap: Record<string, RegExp> = {
      rsa: /^ssh-rsa\s+[A-Za-z0-9+/]+={0,3}(\s+.+)?$/,
      ed25519: /^ssh-ed25519\s+[A-Za-z0-9+/]+={0,3}(\s+.+)?$/,
      // ed25519: /^ssh-ed25519\s+[A-Za-z0-9+/=]+\s*([^@]+@[^ ]+)?$/,
      ecdsa: /^ecdsa-sha2-nistp(256|384|521)\s+[A-Za-z0-9+/]+={0,3}(\s+.+)?$/,
      dsa: /^ssh-dss\s+[A-Za-z0-9+/]+={0,3}(\s+.+)?$/,
    }

    const regex = regexMap[keyType]
    return regex ? regex.test(value) : false
  }, `Please enter a valid ${keyType.toUpperCase()} SSH public key.`)

function isCidr(value: string): boolean {
  return isValidIpv6Cidr(value) || isValidIpv4Cidr(value)
}

export const ipWithPortValidator = customValidator((value: string) => {
  if (isCidr(value)) return false

  // Pure IPv4/IPv6 without port
  if (isValidIpv4Address(value) || isValidIpv6Address(value)) return true

  // IPv4 with port → 192.168.1.10:8080
  if (value.includes('.') && value.includes(':')) {
    const lastColonIndex = value.lastIndexOf(':')
    const ip = value.substring(0, lastColonIndex)
    const port = value.substring(lastColonIndex + 1)

    if (!isValidIpv4Address(ip) || isCidr(ip)) return false

    const portNumber = Number(port)
    return Number.isInteger(portNumber) && portNumber >= 0 && portNumber <= 65535
  }

  // Bracketed IPv6 with port → [2001:db8::1]:53
  const ipv6WithPortMatch = value.match(/^\[([0-9a-fA-F:]+)\]:(\d+)$/)
  if (ipv6WithPortMatch) {
    const [, ip, portStr] = ipv6WithPortMatch
    if (!isValidIpv6Address(ip) || isCidr(ip)) return false

    const portNumber = Number(portStr)
    return Number.isInteger(portNumber) && portNumber >= 0 && portNumber <= 65535
  }

  // Bracketed IPv6 without port → [2001:db8::1]
  const ipv6NoPortMatch = value.match(/^\[([0-9a-fA-F:]+)\]$/)
  if (ipv6NoPortMatch) {
    const [, ip] = ipv6NoPortMatch
    return isValidIpv6Address(ip) && !isCidr(ip)
  }

  // Bare IPv6 with port (lenient but safer)
  const lastColonIndex = value.lastIndexOf(':')
  if (lastColonIndex > 0) {
    const ip = value.substring(0, lastColonIndex)
    const port = value.substring(lastColonIndex + 1)

    // 1️⃣ Only hex digits and colons
    const hexColonCheck = /^[0-9a-fA-F:]+$/.test(ip)

    // 2️⃣ At most one '::' sequence allowed
    const doubleColonMatches = ip.match(/::/g)
    const doubleColonCheck = !doubleColonMatches || doubleColonMatches.length <= 1

    // 3️⃣ Port number validation
    const portNumber = Number(port)
    const portCheck = Number.isInteger(portNumber) && portNumber >= 0 && portNumber <= 65535

    if (hexColonCheck && doubleColonCheck && portCheck) return true
  }

  return false
}, 'Invalid Master IP')
